'use client';

import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/label';
import { toast } from "sonner";
import { registerWithEmailPassword as _registerWithEmailPassword } from '@/lib/auth-api-client';
import { sanitizeRedirect } from '@/lib/redirect';
import { SocialAuthButtons } from './SocialAuthButtons';

interface RegisterFormProps {
  onToggleForm: () => void;
  onSuccess?: () => void;
  inviteEmail?: string;
  inviteName?: string;
  /**
   * Optional internal path (starts with `/`) to land on after registration.
   * Used when the CTA routed the user through `?login=true&redirect_to=...`.
   * Must be pre-sanitized by the caller. Falls back to `/dashboard`.
   */
  redirectTo?: string;
}

const Register: React.FC<RegisterFormProps> = ({ onToggleForm, onSuccess, inviteEmail, inviteName, redirectTo }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [oauthPending, setOAuthPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Get form data - use invite data if form fields are disabled
    const username = inviteName || (formData.get('name') as string) || '';
    const email = inviteEmail || (formData.get('email') as string) || '';
    const password = (formData.get('password') as string) || '';
    const confirmPassword = (formData.get('confirmPassword') as string) || '';
    
    
    // Client-side validation
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    
    try {
      // Use direct fetch for both cases to handle server-side redirect
      const response = await fetch('/api/auth/email/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username.trim(),
          email: inviteEmail || email,
          password,
          isInviteRegistration: !!inviteEmail
        }),
        redirect: 'manual' // Handle redirects manually
      });

      // Re-sanitize at the consumption site so we can't land on an external
      // URL even if a future caller forgets to call sanitizeRedirect.
      const postRegisterTarget = sanitizeRedirect(redirectTo) || '/dashboard';

      // Check if server returned a redirect (successful registration)
      if (response.status === 307 || response.status === 302 || response.type === 'opaqueredirect') {
        toast.success('Registration successful!');
        if (onSuccess) {
          onSuccess();
        }
        // Use window.location for full page redirect to ensure clean state
        window.location.href = postRegisterTarget;
        return;
      }

      // Handle JSON response (errors)
      const result = await response.json();
      if (!result.success) {
        result.message = result.error?.message || result.message || 'Registration failed';
      }

      if (result.success) {
        toast.success(result.message || 'Registration successful!');
        if (onSuccess) {
          onSuccess();
        }
        // Use window.location for full page redirect to ensure clean state
        window.location.href = postRegisterTarget;
      } else {
        // Enhanced error handling with user-friendly messages
        const errorMessage = result.message || 'Registration failed. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Unexpected error during registration:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Social signup creates the account on first use. Invitations stay on the
          form, which is bound to the invited email. */}
      {!inviteEmail && <>
        <SocialAuthButtons
          redirectTo={redirectTo}
          onSuccess={onSuccess ?? (() => window.location.reload())}
          disabled={isLoading}
          onPendingChange={setOAuthPending}
        />
        <div className="flex items-center gap-3 text-xs text-[var(--text-primary-alpha)]" role="separator" aria-label="or sign up with email">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#7ee2ff]/60" />
          <span>or sign up with email</span>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#7ee2ff]/60" />
        </div>
      </>}
      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
        <div className="space-y-2">
          <Label htmlFor="register-name" className="text-[var(--text-primary)]">Name</Label>
          <Input
            id="register-name"
            name="name"
            type="text"
            defaultValue={inviteName || undefined}
            placeholder={inviteName ? inviteName : "Enter your name"}
            className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
            autoComplete="name"
            required
            disabled={Boolean(inviteName)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="register-email" className="text-[var(--text-primary)]">Email</Label>
          <Input
            id="register-email"
            name="email"
            type="email"
            defaultValue={inviteEmail || undefined}
            placeholder={inviteEmail ? inviteEmail : "Enter your email"}
            className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
            autoComplete="email"
            required={!inviteEmail}
            disabled={Boolean(inviteEmail)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="register-password" className="text-[var(--text-primary)]">Password</Label>
          <Input
            id="register-password"
            name="password"
            type="password"
            placeholder="Choose a password"
            className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
            autoComplete="new-password"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="register-confirmPassword" className="text-[var(--text-primary)]">Confirm Password</Label>
          <Input
            id="register-confirmPassword"
            name="confirmPassword"
            type="password"
            placeholder="Confirm your password"
            className="w-full bg-[var(--form-bg-color)] border-[var(--input-border-color)] text-[var(--input-text-color)] placeholder:text-[var(--text-primary-alpha)]"
            autoComplete="new-password"
            required
          />
        </div>

        <Button 
          type="submit" 
          disabled={isLoading || oauthPending}
          className="w-full bg-[var(--secondary-color)] text-[var(--input-text-color)] hover:text-[var(--input-text-color)] hover:bg-[var(--secondary-color)] hover:opacity-80 disabled:opacity-50"
        >
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </Button>

        {!inviteEmail && (
          <Button
            type="button"
            variant="ghost"
            className="w-full text-[var(--text-primary)] hover:text-[var(--input-text-color)]"
            onClick={onToggleForm}
          >
            Already have an account? Login
          </Button>
        )}
      </form>
    </div>
  );
};

export default Register;
